import React, { useState, useEffect, useRef } from 'react';
import { serviceAPI, categoryAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiImage, FiStar } from 'react-icons/fi';
import resolveImage from '../../utils/imageUrl';
import './AdminServices.css';

// ── Service Form Modal ───────────────────────────────────
const ServiceModal = ({ service, categories, onClose, onSaved }) => {
  const isEdit = !!service;
  const fileRef = useRef();

  const [form, setForm] = useState({
    categoryId:       service?.categoryId?._id || service?.categoryId || '',
    name:             service?.name             || '',
    shortDescription: service?.shortDescription || '',
    description:      service?.description      || '',
    basePrice:        service?.basePrice        || '',
    priceUnit:        service?.priceUnit        || 'starting_from',
    duration:         service?.duration         || 60,
    warranty:         service?.warranty         || '',
    isFeatured:       service?.isFeatured       || false,
  });
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(service?.image || null);
  const [saving,       setSaving]       = useState(false);
  const [errors,       setErrors]       = useState({});

  const validate = () => {
    const e = {};
    if (!form.categoryId)           e.categoryId   = 'Category required';
    if (!form.name.trim())          e.name         = 'Name required';
    if (!form.description.trim())   e.description  = 'Description required';
    if (!form.basePrice || isNaN(form.basePrice)) e.basePrice = 'Valid price required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.set('cities', JSON.stringify(['kolkata']));
      fd.set('tags',   JSON.stringify([]));
      fd.set('faqs',   JSON.stringify([]));
      // Use fd.set (not append) so the new file replaces any stale value
      if (imageFile) fd.set('image', imageFile);
      // If no new file selected on edit, explicitly delete so backend
      // knows not to overwrite the existing image path
      else if (isEdit) fd.delete('image');

      if (isEdit) {
        await serviceAPI.update(service._id, fd);
        toast.success('Service updated!');
      } else {
        await serviceAPI.create(fd);
        toast.success('Service created!');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="svc-modal-overlay" onClick={onClose}>
      <div className="svc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="svc-modal-header">
          <h3>{isEdit ? 'Edit Service' : 'Add New Service'}</h3>
          <button className="modal-close-btn" onClick={onClose}><FiX size={18} /></button>
        </div>

        <form className="svc-modal-body" onSubmit={handleSubmit} encType="multipart/form-data">
          {/* Image upload */}
          <div className="img-upload-area" onClick={() => fileRef.current.click()}>
            {imagePreview
              ? <img src={resolveImage(imagePreview)} alt="preview" className="img-preview" />
              : <div className="img-placeholder"><FiImage size={28} /><span>Click to upload image</span></div>
            }
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleImage} />
          </div>

          <div className="svc-form-grid">
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Category *</label>
              <select
                className={`form-input ${errors.categoryId ? 'error' : ''}`}
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              >
                <option value="">Select category</option>
                {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              {errors.categoryId && <span className="field-error">{errors.categoryId}</span>}
            </div>

            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Service Name *</label>
              <input className={`form-input ${errors.name ? 'error' : ''}`}
                type="text" placeholder="e.g. AC Deep Cleaning"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              {errors.name && <span className="field-error">{errors.name}</span>}
            </div>

            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Short Description</label>
              <input className="form-input" type="text" placeholder="One-line summary"
                value={form.shortDescription}
                onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Full Description *</label>
              <textarea className={`form-input ${errors.description ? 'error' : ''}`}
                rows={4} placeholder="Detailed description of what's included..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                style={{ resize: 'vertical' }}
              />
              {errors.description && <span className="field-error">{errors.description}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Base Price (₹) *</label>
              <input className={`form-input ${errors.basePrice ? 'error' : ''}`}
                type="number" min="0" placeholder="399"
                value={form.basePrice} onChange={(e) => setForm({ ...form, basePrice: e.target.value })}
              />
              {errors.basePrice && <span className="field-error">{errors.basePrice}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Price Type</label>
              <select className="form-input" value={form.priceUnit}
                onChange={(e) => setForm({ ...form, priceUnit: e.target.value })}>
                <option value="starting_from">Starting From</option>
                <option value="fixed">Fixed Price</option>
                <option value="per_hour">Per Hour</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Duration (minutes)</label>
              <input className="form-input" type="number" min="15" placeholder="60"
                value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Warranty / Guarantee</label>
              <input className="form-input" type="text" placeholder="e.g. 30 days warranty"
                value={form.warranty} onChange={(e) => setForm({ ...form, warranty: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="featured-toggle">
                <input type="checkbox" checked={form.isFeatured}
                  onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
                />
                <span>Mark as Featured (shows on homepage)</span>
              </label>
            </div>
          </div>

          <div className="svc-modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Admin Services Page ─────────────────────────────
const AdminServices = () => {
  const [services,   setServices]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [modal,      setModal]      = useState(null); // null | 'add' | service object

  const loadData = async () => {
    setLoading(true);
    try {
      const [svcRes, catRes] = await Promise.all([
        serviceAPI.getAll({ city: 'kolkata', limit: 100 }),
        categoryAPI.getAll(),
      ]);
      setServices(svcRes.data.data || []);
      setCategories(catRes.data.data || []);
    } catch { toast.error('Failed to load services'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const handleDelete = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"? This cannot be undone.`)) return;
    try {
      await serviceAPI.delete(svc._id);
      toast.success('Service deleted.');
      loadData();
    } catch { toast.error('Delete failed'); }
  };

  const getCatName = (svc) =>
    svc.categoryId?.name ||
    categories.find(c => c._id === svc.categoryId)?.name || '—';

  return (
    <div className="admin-services">
      <div className="admin-svc-header">
        <div>
          <h3 style={{ marginBottom: 2 }}>Manage Services</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--brand-muted)', margin: 0 }}>
            {services.length} services · Kolkata
          </p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setModal('add')}>
          <FiPlus size={15} /> Add Service
        </button>
      </div>

      {loading ? (
        <div className="svc-grid">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="skeleton" style={{ height: 200, borderRadius: 'var(--radius-lg)' }} />
          ))}
        </div>
      ) : services.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 'var(--sp-12) var(--sp-8)' }}>
          <p style={{ marginBottom: 'var(--sp-5)' }}>No services yet. Add your first one!</p>
          <button className="btn btn-primary" onClick={() => setModal('add')}>
            <FiPlus size={15} /> Add Service
          </button>
        </div>
      ) : (
        <div className="svc-grid">
          {services.map((svc) => (
            <div key={svc._id} className="svc-card">
              <div className="svc-card-img">
                {svc.image
                  ? <img src={resolveImage(svc.image)} alt={svc.name} />
                  : <div className="svc-img-placeholder">🧹</div>}
                {svc.isFeatured && (
                  <span className="svc-featured-chip"><FiStar size={10} /> Featured</span>
                )}
              </div>
              <div className="svc-card-body">
                <span className="svc-cat-label">{getCatName(svc)}</span>
                <p className="svc-name">{svc.name}</p>
                <p className="svc-price">
                  {svc.priceUnit === 'fixed' ? '' : 'From '}
                  <strong>₹{svc.basePrice}</strong>
                </p>
              </div>
              <div className="svc-card-actions">
                <button className="svc-action-btn edit" onClick={() => setModal(svc)} title="Edit">
                  <FiEdit2 size={14} /> Edit
                </button>
                <button className="svc-action-btn delete" onClick={() => handleDelete(svc)} title="Delete">
                  <FiTrash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ServiceModal
          service={modal === 'add' ? null : modal}
          categories={categories}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadData(); }}
        />
      )}
    </div>
  );
};

export default AdminServices;
